import React from 'react';
import {
  Box,
  Checkbox,
  Divider,
  InputAdornment,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { InputLabelBase } from '../../../../../../components/inputs/InputLabelBase';
import { StyledButton } from '../../../../renew-quote/styles';
import RetentionField from '../../views/underwriter/components/rater-x-components/global/RetentionField';
import { CleaveInput } from '../../../../../../components/inputs/CurrencyField';
import { useUnderwriterContext } from '../../views/underwriter/provider/UnderwriterProvider';
import type { Nullable } from '../../../../../../types/common/utility';
import productStatics from '../../../../../_statics/product.statics.json';

type SharedLimitProps = {
  sharedLimit: number | null;
  sharedLimitComponents: Nullable<{ value: string }[]>;
  rules: {
    from?: number;
    to?: number;
    step?: number;
    options?: string[];
  };
  setGlobalValue: (value: any) => void;
  componentOptions: string[];
  sharedLimitComponentRules: any;
};

const SharedLimit = (props: SharedLimitProps) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const hasComponents = !!props.sharedLimitComponents?.length;

  const handleSelectClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!hasComponents) {
      setAnchorEl(event.currentTarget);
      event.stopPropagation();
    }
  };

  return (
    <>
      <InputLabelBase
        style={{
          padding: 0,
          paddingBottom: '0.25rem',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Shared Limit
      </InputLabelBase>

      <Box display="flex">
        <Box flex={1} sx={{ position: 'relative' }}>
          {!hasComponents && (
            <Box
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                zIndex: 2,
              }}
              onClick={handleSelectClick}
            />
          )}

          <RetentionField
            variant="outlined"
            size="small"
            name="sharedLimit"
            InputProps={{
              inputComponent: CleaveInput,
              endAdornment: (
                <SharedLimitIcon
                  sharedLimitComponents={props.componentOptions}
                  componentValues={props.sharedLimitComponents}
                  allMustBeSelected={
                    props.sharedLimitComponentRules?.allMustBeSelected
                  }
                  setGlobalValue={props.setGlobalValue}
                  anchorEl={anchorEl}
                  setAnchorEl={setAnchorEl}
                />
              ),
            }}
            min={Number(props.rules?.from)}
            max={Number(props.rules?.to)}
            step={props.rules?.step}
            value={props.sharedLimit ? Number(props.sharedLimit) : ''}
            onChange={props.setGlobalValue}
            inputProps={{
              min: props.rules?.from,
              max: props.rules?.to,
              step: props.rules?.step,
            }}
            sx={{
              width: '100%',
              '& .MuiInputBase-input': {
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
              },
              '& .MuiInputBase-root': {
                paddingRight: '0 !important',
              },
              '& .Mui-disabled': {
                opacity: 0.65,
              },
            }}
          />
        </Box>
      </Box>
    </>
  );
};

export default SharedLimit;

const SharedLimitIcon = ({
  anchorEl,
  setAnchorEl,
  ...props
}: {
  anchorEl: null | HTMLElement;
  setAnchorEl: (e: any) => void;
  setGlobalValue: (value: any) => void;
  sharedLimitComponents: Nullable<string[]>;
  componentValues: Nullable<{ value: string }[]>;
  allMustBeSelected?: boolean;
}) => {
  const [components, setComponents] = React.useState<Set<string>>(
    () => new Set(props.componentValues?.map((v) => v.value) ?? [])
  );

  React.useEffect(() => {
    if (props.componentValues) {
      setComponents(new Set(props.componentValues.map((v) => v.value)));
    }
  }, [props.componentValues]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleApply = () => {
    setAnchorEl(null);
    props.setGlobalValue({
      sharedLimitComponents: {
        values: Array.from(components).map((c) => ({
          value: c,
        })),
      },
    });
  };

  const open = Boolean(anchorEl);
  const id = open ? 'shared-limit-popover' : undefined;

  return (
    <InputAdornment
      position="end"
      sx={{
        display: 'flex',
        height: '3rem',
        width: '3rem',
        marginLeft: 0,
        borderTopRightRadius: 3,
        borderBottomRightRadius: 3,

        backgroundColor: (theme) =>
          components.size > 0
            ? theme.config.colors.cowbellBlue
            : theme.palette.background.active,
      }}
    >
      <Tooltip title="Select shared limit components" enterDelay={500}>
        <Box
          sx={{
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            padding: '0.25rem',

            color: (theme) =>
              components.size > 0
                ? theme.config.colors.white
                : theme.palette.text.primary,
          }}
          onClick={handleClick}
        >
          <AttachFileIcon fontSize="medium" />
        </Box>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ padding: 1.25, maxWidth: 300 }}>
          <Typography variant="body2">
            Select the shared limit components
          </Typography>

          <Box display="flex" alignItems="center" gap="0.25rem">
            {(props.sharedLimitComponents as string[])?.map((comp) => {
              const compTitle =
                productStatics.ProductTypes[
                  comp as keyof typeof productStatics.ProductTypes
                ].short;
              const isSelected = components.has(comp);

              return (
                <Box key={comp} display="flex" alignItems="center">
                  <Checkbox
                    checked={isSelected}
                    disabled={isSelected && props.allMustBeSelected}
                    onChange={() => {
                      setComponents((prev) => {
                        if (prev.has(comp)) {
                          prev.delete(comp);
                          return new Set(prev);
                        }
                        return new Set(prev.add(comp));
                      });
                    }}
                  />
                  <span style={{ textTransform: 'capitalize' }}>
                    {compTitle}
                  </span>
                </Box>
              );
            })}
          </Box>
        </Box>

        <Divider sx={{ margin: '0 0.25rem' }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: 1.25,
          }}
        >
          <StyledButton
            onClick={handleApply}
            sx={{ minWidth: '5rem', fontSize: '0.85rem' }}
            size="small"
          >
            Apply
          </StyledButton>
        </Box>
      </Popover>
    </InputAdornment>
  );
};

export const UwSharedLimitWithCtx = () => {
  const { state, handlers } = useUnderwriterContext();
  return (
    <SharedLimit
      sharedLimit={state.global?.value?.props?.sharedLimit?.value}
      sharedLimitComponents={
        state.global?.value?.props?.sharedLimitComponents?.values as any
      }
      componentOptions={
        state.global.rules?.sharedLimitComponents?.options as string[]
      }
      setGlobalValue={handlers.setGlobalValue}
      rules={state.global.rules?.sharedLimit as any}
      sharedLimitComponentRules={
        state.global.rules?.sharedLimitComponents as any
      }
    />
  );
};
